import { useBookingHistory } from "../bookingHistory"
import { communicateProductInUrl, productSlugFromSearch } from "../catalogue"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "draftRestored" | "product" | "activeSlug" | "phase" | "postcode" | "setJourneyStep" | "setBookingPane" | "setCalendarOpen" | "setError" | "checkoutGeneration" | "availabilityController" | "setPhase">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "workflowStep" | "choice" | "calendarRangeError" | "meta" | "checkoutResumable" | "deliveryContactRequired">

export function useCheckoutNavigation(inputs: Inputs) {
    const {
        draftRestored,
        product,
        activeSlug,
        phase,
        workflowStep,
        deliveryContactRequired,
        choice,
        postcode,
        calendarRangeError,
        meta,
        checkoutResumable,
        setJourneyStep,
        setBookingPane,
        setCalendarOpen,
        setError,
        checkoutGeneration,
        availabilityController,
        setPhase,
    } = inputs
    const navigateWorkflow = useBookingHistory({
        ready: draftRestored && Boolean(product && product.slug === activeSlug) && phase !== "loading",
        locked: ["paying", "confirming", "complete"].includes(phase),
        step: workflowStep,
        resolve: (requested) => {
            if (["paying", "confirming", "complete"].includes(phase)) return workflowStep
            if (productSlugFromSearch(window.location.search) !== activeSlug) {
                communicateProductInUrl(activeSlug)
                return "dates"
            }
            if (requested === "dates") return "dates"
            if (deliveryContactRequired || !choice || !postcode.trim() || calendarRangeError) return "dates"
            if (requested === "addons") return meta.type !== "dehumidifier" ? "addons" : "dates"
            if (!checkoutResumable()) return "dates"
            return "payment"
        },
        apply: (step) => {
            setJourneyStep(step === "dates" || step === "addons" ? "booking" : "payment")
            setBookingPane(step === "addons" ? "addons" : "selection")
            // Replacing the same step (initialization or a closed date) must
            // preserve its warning and any open date picker.
            if (step !== workflowStep) {
                setCalendarOpen(false)
                setError("")
                window.scrollTo({ top: 0, behavior: "instant" })
            }
        },
        onNavigate: () => {
            if (["paying", "confirming", "complete"].includes(phase)) return
            // A late quote/details response must not move the user forward again.
            checkoutGeneration.current += 1
            availabilityController.current?.abort()
            setPhase("ready")
        },
    })

    return {
        navigateWorkflow,
    }
}
